import { Key } from '@phosphor-icons/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { AuthCard, AuthCardHead } from '../components/auth/AuthCard';
import {
  AuthForm,
  AuthField,
  AuthFormError,
  authInputClassName,
} from '../components/auth/AuthForm';
import { Button } from '../components/ui/Button';
import { PasswordInput } from '../components/ui/PasswordInput';
import { authAPI } from '../data/auth';
import pt from '../locales/pt';
import { cn } from '../utils/cn';

const strongPassword = z
  .string()
  .min(8, pt.auth.errors.passwordWeak)
  .regex(/[A-Za-z]/, pt.auth.errors.passwordWeak)
  .regex(/[0-9]/, pt.auth.errors.passwordWeak);

const schema = z
  .object({
    new_password1: strongPassword,
    new_password2: z.string(),
  })
  .refine((data) => data.new_password1 === data.new_password2, {
    path: ['new_password2'],
    message: pt.auth.errors.passwordsDontMatch,
  });

function PasswordStrength({ value = '' }) {
  const score =
    (value.length >= 8 ? 1 : 0) +
    (/[A-Za-z]/.test(value) ? 1 : 0) +
    (/[0-9]/.test(value) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(value) ? 1 : 0);
  const filled = Math.min(4, score);

  return (
    <div className="flex flex-col gap-space-2">
      <div className="flex gap-space-1">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1.5 flex-1 rounded-ds-full bg-border',
              i < filled && 'bg-sage-500',
            )}
          />
        ))}
      </div>
      <p className="text-type-body-sm text-muted-foreground">{pt.auth.resetPassword.strengthHint}</p>
    </div>
  );
}

export function ResetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { new_password1: '', new_password2: '' },
  });

  const password1 = useWatch({ control, name: 'new_password1' });

  const onSubmit = async (data) => {
    const response = await authAPI.confirmPasswordReset({
      uid,
      token,
      new_password1: data.new_password1,
      new_password2: data.new_password2,
    });
    if (!response.success) {
      const message =
        typeof response.error === 'string'
          ? response.error
          : response.error?.token?.[0] ||
            response.error?.uid?.[0] ||
            response.error?.new_password2?.[0] ||
            response.error?.detail ||
            pt.auth.errors.generic;
      setError('root.server', { message });
      return;
    }
    navigate('/sign-in', { replace: true, state: { passwordReset: true } });
  };

  if (!uid || !token) {
    return (
      <AuthCard badge={<Key className="size-6" weight="regular" aria-hidden />}>
        <AuthCardHead
          title={pt.auth.resetPassword.invalidTitle}
          subtitle={pt.auth.resetPassword.invalidSubtitle}
        />
        <AuthFormError message={pt.auth.resetPassword.invalidBanner} />
        <Button asChild size="lg" className="w-full">
          <Link to="/forgot-password">{pt.auth.resetPassword.requestNewLink}</Link>
        </Button>
        <p className="text-center">
          <Link
            to="/sign-in"
            className="text-type-body-sm font-semibold text-sage-700 hover:underline dark:text-sage-400"
          >
            {pt.auth.resetPassword.backToSignIn}
          </Link>
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard badge={<Key className="size-6" weight="regular" aria-hidden />}>
      <AuthCardHead
        title={pt.auth.resetPassword.title}
        subtitle={pt.auth.resetPassword.subtitle}
      />
      <AuthForm onSubmit={handleSubmit(onSubmit)}>
        <AuthFormError message={errors.root?.server?.message} />
        <div className="flex flex-col gap-space-4">
          <AuthField
            id="reset-password1"
            label={pt.auth.fields.passwordNew}
            error={errors.new_password1?.message}
          >
            <PasswordInput
              id="reset-password1"
              autoComplete="new-password"
              invalid={!!errors.new_password1}
              placeholder={pt.auth.fields.passwordPlaceholder}
              className={authInputClassName}
              {...register('new_password1')}
            />
          </AuthField>
          <AuthField
            id="reset-password2"
            label={pt.auth.fields.passwordConfirm}
            error={errors.new_password2?.message}
          >
            <PasswordInput
              id="reset-password2"
              autoComplete="new-password"
              invalid={!!errors.new_password2}
              placeholder={pt.auth.fields.passwordConfirmPlaceholder}
              className={authInputClassName}
              {...register('new_password2')}
            />
          </AuthField>
        </div>
        <PasswordStrength value={password1} />
        <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
          {isSubmitting ? pt.common.loading : pt.auth.resetPassword.submit}
        </Button>
        <p className="text-center">
          <Link
            to="/sign-in"
            className="text-type-body-sm font-semibold text-sage-700 hover:underline dark:text-sage-400"
          >
            {pt.auth.resetPassword.backToSignIn}
          </Link>
        </p>
      </AuthForm>
    </AuthCard>
  );
}
